import { Sentry } from '@opendoor/observability';
import { ListenerEffectAPI } from '@reduxjs/toolkit';

import { GoogleProtobuf_StructInput } from 'src/__generated__/globalTypes';

import { AppDispatch, RootState } from '.';
import { api } from './api';
import { ExperimentFlag } from './experimentFlags';

/**
 * @returns a promise that is fulfilled whenever the current or next in
 * progress updateAnswers query completes
 */
export const updateAnswersCompleted = async (
  listenerApi: ListenerEffectAPI<RootState, AppDispatch>,
) => {
  const state = listenerApi.getState();
  if (!state.ui.flowInitialized) {
    return;
  }

  const updateAnswerPending = Object.values(state.gql.mutations).some(
    (mutation) => mutation?.status === 'pending' && mutation?.endpointName === 'updateAnswers',
  );

  if (updateAnswerPending) {
    return listenerApi
      .take((action) => {
        return !!api.endpoints.updateAnswers.matchFulfilled(action);
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error(
          'Failed while trying to wait for updateAnswers, did we add an event between answer submit and fulfilled?',
          e,
        );
      });
  }
};

export const loadFeatureFlags = async (
  listenerApi: ListenerEffectAPI<RootState, AppDispatch>,
  keys: string[],
  props: GoogleProtobuf_StructInput,
) => {
  const state = listenerApi.getState();
  // load our various flags
  const address = state.sellerInput.address ? state.sellerInput.address : null;
  const addressToken = address ? address.uuid : null;

  if (!addressToken) {
    Sentry.captureMessage(`Load feature flag requested for ${keys}, but we have no addressToken!`);
  }

  const { isUninitialized, data } = api.endpoints.getPropertyFeatureFlags.select({
    addressToken,
    keys,
    props,
  })(state);

  if (!isUninitialized && data) {
    return data.variant;
  }

  // attempt variant query and return result
  return listenerApi.dispatch(
    api.endpoints.getPropertyFeatureFlags.initiate({
      addressToken,
      keys,
      props,
    }),
  );
};

/**
 *
 * This can be used to kick off loading an experiment flag. If the flag has
 * already been loaded (with the same request parameters) then we will not
 * request it again
 *
 * @returns a promise that is fulfilled when we have loaded the variant for the
 * passed in experiment flag
 */
export const loadExperiment = async (
  listenerApi: ListenerEffectAPI<RootState, AppDispatch>,
  flag: ExperimentFlag,
  triggerImpression = false,
) => {
  const state = listenerApi.getState();
  // load our various flags
  const experimentEntityId = state.sellerInput.experimentEntityId
    ? state.sellerInput.experimentEntityId
    : null;
  const customerUUID = state.sellerInput.customerUUID ? state.sellerInput.customerUUID : null;

  if (!customerUUID && !experimentEntityId) {
    // TODO - there's not a lot for us to do in this situation, but maybe we should Sentry?
    // eslint-disable-next-line no-console
    console.error(
      `Load experiment reqested for ${flag}, but we have no customer uuid or experimentEntityId!`,
    );
  }

  const { isUninitialized, data } = api.endpoints.getVariantQuery.select({
    experimentEntityId,
    customerUUID,
    experimentFlag: flag,
    triggerImpression,
  })(state);

  if (!isUninitialized && data) {
    return data.variant;
  }

  // attempt variant query and return result
  return listenerApi.dispatch(
    api.endpoints.getVariantQuery.initiate({
      experimentEntityId,
      customerUUID,
      experimentFlag: flag,
      triggerImpression,
    }),
  );
};

/**
 * Similar to loadExperiment but returns the variant value of the experiment flag
 */
export const getExperiment = async (
  listenerApi: ListenerEffectAPI<RootState, AppDispatch>,
  flag: ExperimentFlag,
  triggerImpression = false,
) => {
  await loadExperiment(listenerApi, flag, triggerImpression);
  const updatedState = listenerApi.getState();
  return updatedState.experimentFlags[flag];
};
